// Styles
import "./app-row.scss";
// Components
import { StatusChip } from "../../../../../../../../../../shared/components/status-chip/StatusChip";
import { CompactButton } from "../../../../../../../../../../shared/components/buttons/compact-button/CompactButton";
import { useDispatch, useSelector } from "react-redux";
import { toggleHighlightedApp } from "../../../../../../../../../../shared/store/slices/app.slice";
import { RootState } from "../../../../../../../../../../shared/store/store";
import { useState } from "react";
import {
	useAppTicketAction,
	type AppTicketAction,
} from "../../service/app-ticket.service";
import { CircleLoader } from "../../../../../../../../../../shared/components/circle-loader/CircleLoader";
import { normalizeStatusKey } from "../../../../../../../../../../shared/utils/normalize-status-label";

interface Props {
	id: string;
	status: string;
	cost: number;
	is_linked: boolean;
	all_accounts_available: boolean;
	accounts: string[];
	dimmed?: boolean;
	isTicketRow?: boolean;
	ticketId?: number;
}

export const AppRow = ({
	id,
	status,
	cost,
	is_linked,
	all_accounts_available,
	accounts,
	dimmed,
	isTicketRow = false,
	ticketId,
}: Props) => {
	const dispatch = useDispatch<any>();
	const { mutateAsync } = useAppTicketAction();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const isActive = useSelector((state: RootState) =>
		state.app.highlightedApps.some((h) => h.appId === id),
	);

	const handleIconsClick = () => {
		dispatch(toggleHighlightedApp({ appId: id, accounts }));
	};

	const ticketActionDisabled = ticketId == null || isLoading;

	const runTicketAction = async (action: AppTicketAction) => {
		if (ticketId == null) return;
		setIsLoading(true);
		try {
			await mutateAsync({ ticketId, action });
		} finally {
			setIsLoading(false);
		}
		setTimeout(() => setIsLoading(false), 2000);
	};

	const handleReject = () => {
		void runTicketAction(false);
	};

	const handleAccept = () => {
		void runTicketAction(true);
	};

	const showTicketActions =
		isTicketRow && normalizeStatusKey(status) === "pending";

	return (
		<div className={`app-row${dimmed ? " app-row--dimmed" : ""}`}>
			<div className='app-row__wrapper'>
				{isLoading && (
					<div className='app-row__loader-wrapper'>
						<div className='app-row__loader-wrapper_bg' />
						<div className='app-row__loader'>
							<CircleLoader size='small' />
						</div>
					</div>
				)}
				<div
					className={`app-row__wrapper_top${showTicketActions ? " app-row__wrapper_top--pending" : ""}`}>
					<div className='app-row__name'>
						<div className='app-row__name_id break-text' data-copyable>
							{id}
						</div>
						<div
							className={`app-row__name_icons${isActive ? " app-row__name_icons--active" : ""}`}
							onClick={handleIconsClick}>
							<div className='app-row__name_icons-left'>
								{!all_accounts_available && (
									<img
										src='/img/icons/warning.png'
										alt='warning icon'
										title='Доступний не на всіх акаунтах. Натисніть, щоб підсвітити ті, де він є'
										className='app-row__name_icons-warning'
									/>
								)}
							</div>
							<div className='app-row__name_icons-right'>
								{is_linked ? (
									<img
										src='/img/icons/link.png'
										alt='linked icon'
										title='Прив’язаний напряму до вашого акаунта'
										className='app-row__name_icons-link'
									/>
								) : (
									<img
										src='/img/icons/group.png'
										alt='group icon'
										title='Спільний доступ з іншого акаунта'
										className='app-row__name_icons-group'
									/>
								)}
							</div>
						</div>
					</div>
					{showTicketActions ? (
						<div className='app-row__wrapper_actions'>
							<CompactButton
								children='Reject'
								title='Reject'
								theme={"red"}
								className={"small"}
								unactive={ticketActionDisabled}
								onClick={handleReject}
								iconLeft={<img src='/img/icons/close.png' alt='reject icon' />}
							/>
							<CompactButton
								children='Accept'
								title='Accept'
								theme={"green"}
								className={"small"}
								unactive={ticketActionDisabled}
								onClick={handleAccept}
								iconLeft={
									<img src='/img/icons/check-mark.png' alt='accept icon' />
								}
							/>
						</div>
					) : (
						<div className='app-row__status'>
							<StatusChip status={status} />
						</div>
					)}
					<div className='app-row__cost' data-copyable>
						<span data-copy-exclude>$</span>
						{Number(cost).toFixed(2)}
					</div>
				</div>
			</div>
		</div>
	);
};
